import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { HighlightedCode } from "~/pages/manifold-nextgen/components/conversations/HighlightedCode";
import { MermaidDiagram } from "~/pages/manifold-nextgen/components/conversations/artifacts/MermaidDiagram";
import {
  PinnedArtifact,
  RefreshRunStatus,
  StepState,
  WorkflowStep,
  isRefreshable,
} from "~/services/pinnedArtifacts";
import { useArtifactRefreshRun } from "./useArtifactRefreshRun";
import { workflowToMermaid } from "./workflowMermaid";

interface PinnedArtifactWorkflowProps {
  pin: PinnedArtifact;
  open: boolean;
  onClose: () => void;
}

const STATUS_VARIANT: Record<RefreshRunStatus, "info" | "success" | "destructive" | "muted"> = {
  pending: "muted",
  running: "info",
  succeeded: "success",
  failed: "destructive",
};

const KIND_LABEL: Record<WorkflowStep["kind"], string> = {
  sql: "Query",
  code: "Code",
  frozen_input: "Fixed input",
};

// The workflow behind a pinned artifact: the graph that reproduces it, each
// step's code, and the controls to run it again.
//
// The diagram and the step list both come from the pin's frozen recipe rather
// than from live lineage, because the recipe is what a refresh actually runs.
export const PinnedArtifactWorkflow = ({ pin, open, onClose }: PinnedArtifactWorkflowProps) => {
  const { run, isStarting, start } = useArtifactRefreshRun(
    pin.project_id,
    pin.id,
    pin.last_refresh_run_id
  );
  const refreshable = isRefreshable(pin);
  const stateByStep = new Map<string, StepState>(
    (run?.step_states ?? []).map((state) => [state.step_id, state])
  );
  const isRunning = run?.status === "pending" || run?.status === "running";

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{pin.display_name}</SheetTitle>
          <SheetDescription>
            How this artifact was made, and everything it was made from.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-5 overflow-y-auto px-4 pb-6">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              size="sm"
              onClick={() => void start()}
              disabled={!refreshable || isStarting || isRunning}
            >
              {isStarting || isRunning ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="size-3.5" aria-hidden />
              )}
              {isRunning ? "Refreshing…" : "Refresh"}
            </Button>
            {run && <Badge variant={STATUS_VARIANT[run.status]}>{run.status}</Badge>}
          </div>

          {!refreshable && (
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              This artifact can&apos;t be rebuilt: it wasn&apos;t derived from a dataset, so there
              is no query or code to run again.
            </p>
          )}

          {run?.error && <p className="text-xs text-destructive">{run.error}</p>}

          <section>
            <h3 className="mb-2 text-sm font-medium">Workflow</h3>
            <MermaidDiagram
              source={workflowToMermaid(pin.recipe)}
              idPrefix={`workflow-${pin.id}`}
            />
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-medium">Steps ({pin.recipe.steps.length})</h3>
            {pin.recipe.steps.map((step, index) => (
              <StepRow
                key={step.step_id}
                step={step}
                index={index}
                state={stateByStep.get(step.step_id)}
              />
            ))}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

interface StepRowProps {
  step: WorkflowStep;
  index: number;
  state: StepState | undefined;
}

// One step: what it is, where its data came from, and the code that produced
// it. Steps are already in dependency order, so the index reads as the order
// a refresh will run them in.
const StepRow = ({ step, index, state }: StepRowProps) => (
  <div className="rounded-md border border-border">
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
      <span className="text-xs text-muted-foreground">{index + 1}</span>
      <Badge variant="outline">{KIND_LABEL[step.kind]}</Badge>
      <span className="min-w-0 flex-1 truncate text-sm" title={step.summary ?? step.step_id}>
        {step.summary ?? step.step_id}
      </span>
      {state && <Badge variant={STATUS_VARIANT[state.status]}>{state.status}</Badge>}
    </div>
    <div className="flex flex-col gap-2 px-3 py-2">
      {step.source_tables.length > 0 && (
        <p className="text-xs text-muted-foreground">From {step.source_tables.join(", ")}</p>
      )}
      {step.runtime && <p className="text-xs text-muted-foreground">Runs in {step.runtime}</p>}
      {step.frozen_reason && (
        <p className="text-xs text-muted-foreground">Reused as-is — {step.frozen_reason}.</p>
      )}
      {state?.error && <p className="text-xs text-destructive">{state.error}</p>}
      {step.code && (
        <HighlightedCode
          code={step.code}
          language={step.kind === "sql" ? "sql" : "python"}
          className="max-h-64 overflow-auto text-xs"
        />
      )}
    </div>
  </div>
);
